import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ArrowRight, Download, HelpCircle } from "lucide-react";
import { HomeworkShell } from "./HomeworkShell";
import { hwErr, fmtDate } from "./hwApi";
import { hwdApi } from "./HomeworkHome";

const API = process.env.REACT_APP_BACKEND_URL;
const STATUS_LABEL = { draft: "DRAFT", submitted: "WITH SIR", reviewing: "BEING CHECKED", evaluated: "CHECKED",
                       returned_for_correction: "CORRECTIONS REQUESTED", resubmission_requested: "RESUBMIT REQUESTED" };
const Q_STATUS_LABEL = { solved_confidently: "Solved confidently", attempted_unsure: "Attempted but unsure",
                         not_attempted: "Not attempted", doubt: "Needs doubt explanation" };
const Q_EVAL_LABEL = { correct: "CORRECT", partially_correct: "PARTIALLY CORRECT", incorrect: "INCORRECT",
                       not_attempted: "NOT ATTEMPTED", needs_clarification: "NEEDS REVIEW" };
const Q_EVAL_CLS = { correct: "approved", partially_correct: "review", incorrect: "failed", not_attempted: "locked", needs_clarification: "review" };
const SLOTS = [["question_paper", "Question Paper"], ["attempted", "My Solved Homework"], ["solution", "Solution (reference)"]];

function DetailInner() {
  const { submissionId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    hwdApi.get(`/submissions/${submissionId}`).then(({ data: d }) => setData(d)).catch((e) => setError(hwErr(e)));
  }, [submissionId]);

  if (error) return <div className="tp-wrap"><p className="tp-error" data-testid="hwd-error">{error}</p></div>;
  if (!data) return <div className="tp-wrap"><p className="tp-muted">Loading…</p></div>;
  const ev = data.evaluation;
  const perQ = ev?.per_question || [];
  const doubts = data.doubts || {};

  return (
    <div className="tp-wrap" style={{ maxWidth: 760 }} data-testid="hwd-detail">
      <div className="tp-eyebrow">HOMEWORK · {data.submission_id}</div>
      <h1 className="tp-h2" style={{ fontSize: 28 }}>{data.title}</h1>
      <p className="tp-muted">{data.category_label} · {data.subject} · {data.chapter} · {data.topic}{data.subtopic ? ` · ${data.subtopic}` : ""}</p>
      <p>
        <span className={`status-pill ${data.status === "evaluated" ? "approved" : data.status === "draft" ? "locked" : "review"}`} data-testid="hwd-status">
          {STATUS_LABEL[data.status]}</span>
        {data.submitted_at && <span className="tp-muted" style={{ marginLeft: 8, fontSize: 12 }}>Submitted {fmtDate(data.submitted_at)}{data.version > 1 ? ` · version ${data.version}` : ""}</span>}
      </p>

      {(data.status === "returned_for_correction" || data.status === "resubmission_requested") && (
        <div className="tp-panel" style={{ border: "1px solid #f59e0b", marginTop: 12 }} data-testid="hwd-correction-banner">
          <b style={{ color: "#b45309" }}>Pranjal Sir asked for corrections</b>
          <p className="tp-muted" style={{ margin: "6px 0 10px" }}>{ev?.overall_feedback || "Please update your documents and resubmit."}</p>
          <a className="tp-btn small" href={`/homework/new?resume=${data.submission_id}`} data-testid="hwd-resubmit">Update &amp; resubmit <ArrowRight size={14} /></a>
        </div>
      )}

      <div className="tp-panel" style={{ marginTop: 14 }} data-testid="hwd-docs">
        <h2 className="tp-h2" style={{ fontSize: 18, margin: 0 }}>Documents</h2>
        {SLOTS.map(([slot, label]) => {
          const d = data.documents?.[slot];
          return (
            <div className="admin-row" key={slot} style={{ borderTop: "1px solid #e4eaf2", padding: "8px 0" }} data-testid={`hwd-doc-${slot}`}>
              <span className={`status-pill ${d ? "approved" : "locked"}`}>{d ? (d.teacher_reference ? "TEACHER REFERENCE" : "UPLOADED") : slot === "solution" ? "OPTIONAL" : "MISSING"}</span>
              <span style={{ flex: 1, color: "#1e3a5f", fontSize: 13 }}>{label}{d?.name ? ` — ${d.name} · ${d.pages} page(s)` : ""}</span>
              {d && !d.teacher_reference && (
                <a className="tp-btn small ghost" href={`${API}/api/hwd/files/${data.submission_id}/${slot}`} target="_blank" rel="noreferrer" data-testid={`hwd-view-${slot}`}><Download size={13} /> View</a>
              )}
              {d?.teacher_reference && <span className="tp-muted" style={{ fontSize: 11 }}>for teacher's reference</span>}
            </div>
          );
        })}
      </div>

      {(data.question_statuses || []).length > 0 && (
        <div className="tp-panel" style={{ marginTop: 14 }} data-testid="hwd-questions">
          <h2 className="tp-h2" style={{ fontSize: 18, margin: 0 }}>Question-wise record</h2>
          {data.question_statuses.map((q, i) => {
            const pq = perQ.find((x) => x.qno === q.qno);
            const doubt = doubts[q.qno];
            return (
              <div key={q.qno} style={{ borderTop: "1px solid #e4eaf2", padding: "8px 0" }} data-testid={`hwd-q-${i}`}>
                <div className="admin-row" style={{ flexWrap: "wrap", gap: 6 }}>
                  <b style={{ color: "#0b2545", minWidth: 40 }}>{q.qno}</b>
                  <span className="status-pill review">{Q_STATUS_LABEL[q.status]}</span>
                  {pq && <span className={`status-pill ${Q_EVAL_CLS[pq.qstatus]}`} data-testid={`hwd-qeval-${i}`}>{Q_EVAL_LABEL[pq.qstatus]}</span>}
                </div>
                {q.note && <p className="tp-muted" style={{ margin: "4px 0 0", fontSize: 12 }}>Your note: {q.note}</p>}
                {pq?.comment && <p className="tp-note" style={{ color: "#92400e", margin: "4px 0 0" }} data-testid={`hwd-qcomment-${i}`}>Pranjal Sir: {pq.comment}</p>}
                {doubt?.addressed && (
                  <p className="tp-note" style={{ color: "#0369a1", margin: "4px 0 0", background: "#f0f9ff", padding: 8, borderRadius: 8 }} data-testid={`hwd-doubt-${i}`}>
                    <b>Doubt answered:</b> {doubt.explanation}{doubt.comment ? ` — ${doubt.comment}` : ""}</p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {ev && (
        <div className="tp-panel" style={{ marginTop: 14, border: "1px solid #12b76a" }} data-testid="hwd-result">
          <div className="tp-eyebrow">PERSONAL EVALUATION — BY PRANJAL SIR</div>
          <b style={{ fontSize: 30, color: "#15803d" }} data-testid="hwd-score">{ev.score_10}/10</b>
          {[["Overall feedback", ev.overall_feedback], ["Main mistakes", ev.main_mistakes],
            ["Concepts to revise", ev.concepts_to_revise], ["Recommended practice", ev.recommended_practice],
            ["Personal message", ev.personal_message]].filter(([, v]) => v).map(([l, v]) => (
            <p key={l} style={{ margin: "10px 0 0" }} data-testid={`hwd-fb-${l.replace(/\W+/g, "-").toLowerCase()}`}>
              <b style={{ color: "#0b2545", fontSize: 13 }}>{l}:</b> <span className="tp-muted" style={{ fontSize: 13 }}>{v}</span></p>
          ))}
        </div>
      )}

      {Object.keys(doubts).length === 0 && data.question_statuses?.some((q) => ["attempted_unsure", "not_attempted", "doubt"].includes(q.status)) && !ev && (
        <div className="tp-panel" style={{ marginTop: 14, border: "1px dashed #7dd3fc" }} data-testid="hwd-doubts-pending">
          <div className="admin-row"><HelpCircle size={16} style={{ color: "#0369a1" }} />
            <b style={{ color: "#0369a1", fontSize: 14 }}>Questions needing personal attention were shared with Pranjal Sir</b></div>
          <p className="tp-muted" style={{ fontSize: 12, margin: "6px 0 0" }}>He will add explanations when he reviews your homework. Doubts in homework never use your doubt credits.</p>
        </div>
      )}
    </div>
  );
}

export default function HomeworkDocDetail() {
  return <HomeworkShell><DetailInner /></HomeworkShell>;
}
